/**
 * BrandLoader — the platform's loading identity, in two shapes.
 *
 * `intro` is the landing's opening: the screen sits as one solid surface,
 * the logo pops, the wordmark rises letter by letter, a counter runs to
 * 100%, the centre content leaves, and only then do the two halves part
 * like curtains onto a page that is already animating behind them. The
 * overlap is what makes it read as one continuous shot rather than a
 * loading screen that disappears.
 *
 * `loading` is the same identity without the curtains — the standard
 * screen wherever the app has to wait (boot, auth check, route work). Same
 * logo, same wordmark, same bar, so waiting anywhere in the product looks
 * like the same product.
 *
 * Both honour prefers-reduced-motion by skipping straight to the end.
 */
import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import './brand-loader.css';

interface BrandLoaderProps {
  /**
   * `intro`   — abertura da landing (cortinas + contador real).
   * `loading` — tela cheia padrão de espera (boot, auth, rota).
   * `inline`  — a MESMA identidade em tamanho de seção: centrada no
   *             container, sem fundo próprio. É o loader de cards,
   *             painéis e áreas — pra espera parecer o mesmo produto
   *             em qualquer altitude.
   */
  variant?: 'intro' | 'loading' | 'inline';
  /** intro only — fired the moment the curtains start parting, so the
   *  page behind can begin its own entrance under them. */
  onReveal?: () => void;
  /**
   * intro only — REAL readiness, 0..1. The counter chases this value, so
   * 100% means the page behind is genuinely ready rather than a timer
   * having elapsed. Leave undefined for a self-timed intro.
   */
  progress?: number;
}

/** Nothing may hold the visitor hostage to a stalled asset. */
const MAX_WAIT_MS = 9000;

const WORD = 'my360';

/**
 * Estado da marca, compartilhado por TODAS as instâncias desta página.
 *
 * O bug que isto conserta: o loader inline do index.html apresenta a marca e
 * conta até ~12%; o primeiro BrandLoader continua daí e sobe até quase 100%;
 * aí uma SEGUNDA instância monta (o gate global e o intro da landing são dois
 * loaders de tela cheia), não encontra mais a bandeira — porque a primeira a
 * consumiu — e reproduz a abertura inteira do zero antes de saltar para 100%.
 *
 * Era o que se via: a barra quase completa, a logo animando de novo, e o
 * número pulando.
 *
 * A correção é parar de tratar "a marca já apareceu" como um bilhete que se
 * gasta. É uma propriedade da PÁGINA: depois que a marca entrou, ela está na
 * tela, e nenhuma instância seguinte tem o direito de reencená-la. Um
 * carregamento novo da página zera isto naturalmente, porque o módulo é
 * recarregado.
 */
const brand = {
  /** A marca já foi apresentada nesta página (inline loader ou React). */
  presented: false,
  /** Última porcentagem exibida — para a instância seguinte continuar dela. */
  pct: 0,
};

export function BrandLoader({ variant = 'loading', onReveal, progress }: BrandLoaderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [done, setDone] = useState(false);
  /** Live readiness, read by the rAF loop without re-running the effect. */
  const progressRef = useRef(0);
  progressRef.current = progress ?? 0;
  const rafRef = useRef<number | null>(null);

  /**
   * Whether index.html's boot loader already presented the mark.
   *
   * Resolved ONCE per mount and held in a ref, deliberately: StrictMode
   * runs effects twice in dev, and reading-and-clearing this inside the
   * effect made the two passes disagree — the first continued from the
   * boot loader, its cleanup reverted that, and the second replayed the
   * full opening. The mark appeared, vanished and animated in again,
   * which is exactly the "it plays twice" bug.
   */
  const continuingRef = useRef<boolean | null>(null);
  if (continuingRef.current === null) {
    const w = window as { __my360BootShown?: boolean; __my360BootDone?: boolean; __my360BootPct?: number };
    // Continua se a marca já está na tela — venha do loader inline ou de
    // outra instância desta mesma página.
    continuingRef.current = brand.presented || w.__my360BootShown === true;
    if (w.__my360BootShown === true) brand.pct = Math.max(brand.pct, w.__my360BootPct ?? 0);
    brand.presented = true;
    // React owns the counter from here; stop the boot loader's own ticker.
    w.__my360BootDone = true;
  }

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (variant === 'loading' || variant === 'inline') {
      // SEM legenda e SEM varredura indeterminada: a identidade é sempre a
      // porcentagem 0→100%, contínua. Quando não há progresso real, o número
      // avança sozinho e desacelera assintótico perto do fim — quem fecha o
      // loader é o gate, com fade, então "100%" nunca precisa mentir.
      const continuing = continuingRef.current === true;
      const counter = { v: continuing ? brand.pct : 0 };
      const countEl = root.querySelector('.bl-count');
      const fillEl = root.querySelector<HTMLElement>('.bl-bar-fill');

      const tick = () => {
        // Progresso real quando informado; senão, assíntota suave até 97.
        const target = progressRef.current > 0 ? progressRef.current * 100 : 97;
        counter.v += (target - counter.v) * 0.012;
        // Nunca anda para trás: a instância seguinte parte daqui.
        brand.pct = Math.max(brand.pct, counter.v);
        const n = Math.min(100, Math.round(counter.v));
        if (countEl) countEl.textContent = `${n}%`;
        if (fillEl) fillEl.style.width = `${n}%`;
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      if (reduced || continuing) {
        // Handoff do boot loader (ou reduced motion): os elementos JÁ estão
        // na tela — repetir a entrada era o "corte" que reiniciava a
        // animação a cada carregamento de página.
        gsap.set('.bl-logo', { opacity: 1, scale: 1, y: 0 });
        gsap.set('.bl-char', { opacity: 1, y: 0 });
        return () => {
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
      }
      const ctx = gsap.context(() => {
        gsap.to('.bl-logo', { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.6)' });
        gsap.to('.bl-char', { opacity: 1, y: 0, duration: 0.5, stagger: 0.04, ease: 'power3.out', delay: 0.15 });
      }, root);
      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        ctx.revert();
      };
    }

    if (reduced) {
      onReveal?.();
      setDone(true);
      return;
    }

    const ctx = gsap.context(() => {
      // Pick the number up where the inline boot loader left it, so the
      // handover continues the count instead of snapping back to zero.
      const counter = { v: continuingRef.current === true ? brand.pct : 0 };
      const countEl = root.querySelector('.bl-count');
      const fillEl = root.querySelector<HTMLElement>('.bl-bar-fill');

      // Continue from the boot loader's state on a cold load; play the
      // full opening when reached by client-side navigation.
      const continuing = continuingRef.current === true;

      const tl = gsap.timeline();

      if (continuing) {
        tl.set(['.bl-logo', '.bl-char'], { opacity: 1, scale: 1, y: 0 })
          .set('.bl-meta', { opacity: 1 });
      } else {
        // Beat 1 — identity.
        tl.fromTo('.bl-logo', { opacity: 0, scale: 0.7, y: 8 }, { opacity: 1, scale: 1, y: 0, duration: 0.55, ease: 'back.out(1.6)' })
          .to('.bl-char', { opacity: 1, y: 0, duration: 0.6, stagger: 0.045, ease: 'power3.out' }, '-=0.3')
          .to('.bl-meta', { opacity: 1, duration: 0.3 }, '-=0.4');
      }

      // Beat 2 — REAL progress. The number chases actual readiness instead
      // of running a fixed animation, so hitting 100% means the page behind
      // is genuinely loaded. Without this the curtains opened on a page
      // that was still fetching, which is the delay you feel afterwards.
      const runCounter = () =>
        new Promise<void>((resolve) => {
          const started = performance.now();
          const step = () => {
            const elapsed = performance.now() - started;
            // Ease toward the real value so the number never jumps or stalls
            // dead; the floor keeps it visibly moving on a fast connection.
            const target = Math.max(progressRef.current, Math.min(0.9, elapsed / 2600));
            counter.v += (target * 100 - counter.v) * 0.08;
            if (elapsed > MAX_WAIT_MS) counter.v = 100;
            // Publica para a instância seguinte, se houver: o número é da
            // página, não deste componente.
            brand.pct = Math.max(brand.pct, counter.v);
            const n = Math.min(100, Math.round(counter.v));
            if (countEl) countEl.textContent = `${n}%`;
            if (fillEl) fillEl.style.width = `${n}%`;
            // Only finish once readiness is real AND the number caught up.
            if ((progressRef.current >= 1 && n >= 100) || elapsed > MAX_WAIT_MS) {
              if (countEl) countEl.textContent = '100%';
              if (fillEl) fillEl.style.width = '100%';
              resolve();
              return;
            }
            rafRef.current = requestAnimationFrame(step);
          };
          rafRef.current = requestAnimationFrame(step);
        });

      tl.call(() => {
        void runCounter().then(() => {
          // Beats 3 and 4 — the centre leaves first so the curtains open
          // onto a clean stage, then they part while the page behind is
          // already animating.
          gsap.timeline({ onComplete: () => setDone(true) })
            .to('.bl-center', { opacity: 0, y: -24, duration: 0.35, ease: 'power2.in' }, '+=0.1')
            .add(() => onReveal?.())
            .to('.bl-half-l', { xPercent: -100, duration: 0.9, ease: 'power4.inOut' })
            .to('.bl-half-r', { xPercent: 100, duration: 0.9, ease: 'power4.inOut' }, '<');
        });
      });
    }, root);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ctx.revert();
    };
  }, [variant, onReveal]);

  if (done) return null;

  return (
    <div ref={rootRef} className={`bl bl-${variant}`} role="status" aria-live="polite">
      {variant === 'intro' && (
        <>
          <div className="bl-half bl-half-l" />
          <div className="bl-half bl-half-r" />
        </>
      )}
      <div className="bl-center">
        <img className="bl-logo" src="/logo.png" alt="" width={72} height={72} />
        <div className="bl-word">
          {WORD.split('').map((c, i) => (
            <span className="bl-char" key={i}>{c}</span>
          ))}
        </div>
        {/* A BARRA é o elemento alinhado com logo e wordmark; o número fica
            FORA do fluxo (absoluto, à esquerda) pra nunca desalinhar o
            conjunto. Sem legendas: a identidade é só a porcentagem. */}
        <div className="bl-meta">
          <span className="bl-count">0%</span>
          <span className="bl-bar"><span className="bl-bar-fill" /></span>
        </div>
      </div>
    </div>
  );
}

/**
 * BrandBar — a menor unidade da identidade de loading: só a barra teal
 * varrendo. Pra onde nem o wordmark cabe (overlay de imagem carregando
 * no viewer, célula de tabela). CSS puro, sem gsap.
 */
export function BrandBar({ className = '' }: { className?: string }) {
  return <span className={`bl-microbar ${className}`} role="status" aria-live="polite" />;
}

export default BrandLoader;
